import React, { useState } from 'react';
import { Button, Card, Row, Col, Typography, Input, Form, message } from 'antd';
import {
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useTheme } from '../components/ParticleBackground';
import useResponsive from '../hooks/useResponsive';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const Contact = () => {
  const { theme } = useTheme();
  const { isMobile } = useResponsive();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const getThemeStyles = () => {
    if (theme === 'dark') {
      return {
        titleColor: '#f8fafc',
        textColor: '#cbd5e1',
        subtitleColor: '#94a3b8',
        cardBg: 'rgba(30, 41, 59, 0.8)',
        cardBorder: 'rgba(71, 85, 105, 0.3)',
        heroBg: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
        sectionBg: 'rgba(15, 23, 42, 0.5)',
        accentColor: '#667eea',
        inputBg: 'rgba(30, 41, 59, 0.6)',
        inputBorder: 'rgba(71, 85, 105, 0.4)'
      };
    } else {
      return {
        titleColor: '#1f2937',
        textColor: '#4b5563',
        subtitleColor: '#6b7280',
        cardBg: 'rgba(255, 255, 255, 0.9)',
        cardBorder: 'rgba(226, 232, 240, 0.6)',
        heroBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
        sectionBg: 'rgba(248, 250, 252, 0.5)',
        accentColor: '#667eea',
        inputBg: 'rgba(255, 255, 255, 0.9)',
        inputBorder: 'rgba(226, 232, 240, 0.8)'
      };
    }
  };

  const themeStyles = getThemeStyles();

  const contactInfo = [
    {
      icon: <PhoneOutlined style={{ fontSize: '28px', color: themeStyles.accentColor }} />,
      title: 'Phone',
      content: '+91 98765 43210',
      link: 'tel:+919876543210'
    },
    {
      icon: <MailOutlined style={{ fontSize: '28px', color: themeStyles.accentColor }} />,
      title: 'Email',
      content: 'hello@ekahhealth.com',
      link: 'mailto:hello@ekahhealth.com'
    },
    {
      icon: <EnvironmentOutlined style={{ fontSize: '28px', color: themeStyles.accentColor }} />,
      title: 'Location',
      content: 'Bangalore, Karnataka, India',
      link: null
    },
    {
      icon: <ClockCircleOutlined style={{ fontSize: '28px', color: themeStyles.accentColor }} />,
      title: 'Working Hours',
      content: 'Mon - Sat: 9:00 AM - 6:00 PM',
      link: null
    }
  ];

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success('Thank you for reaching out! We will get back to you soon.');
      form.resetFields();
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: themeStyles.heroBg,
        padding: isMobile ? '40px 16px 20px' : '80px 24px 60px',
        textAlign: 'center',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Title
            level={1}
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: 'bold',
              color: themeStyles.titleColor,
              marginBottom: isMobile ? '12px' : '24px',
              lineHeight: '1.1'
            }}
          >
            Get in <span style={{ color: themeStyles.accentColor }}>Touch</span>
          </Title>
          <Paragraph style={{
            fontSize: 'clamp(16px, 2vw, 20px)',
            color: themeStyles.textColor,
            maxWidth: '600px',
            margin: '0 auto 20px',
            lineHeight: '1.7'
          }}>
            Have questions about our naturopathy services? We're here to help you on your journey to holistic wellness.
          </Paragraph>
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'column',
        gap: 0
      }}>
        {/* Contact Form Section - Order 1 on Mobile */}
        <div style={{
          order: isMobile ? 1 : 2,
          background: themeStyles.sectionBg,
          padding: isMobile ? '20px 16px' : '80px 24px',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '24px' : '50px' }}>
              <Title level={2} style={{ color: themeStyles.titleColor, marginBottom: '16px', fontSize: isMobile ? '24px' : '30px' }}>
                Send Us a Message
              </Title>
              <Paragraph style={{
                color: themeStyles.subtitleColor,
                fontSize: '16px'
              }}>
                Fill out the form below and we'll get back to you within 24 hours
              </Paragraph>
            </div>

            <Card
              style={{
                background: themeStyles.cardBg,
                border: `1px solid ${themeStyles.cardBorder}`,
                borderRadius: '20px',
                backdropFilter: 'blur(20px)',
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.3)'
                  : '0 20px 60px rgba(0, 0, 0, 0.1)'
              }}
              bodyStyle={{ padding: isMobile ? '20px' : '40px' }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark={false}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="name"
                      label={<span style={{ color: themeStyles.titleColor, fontWeight: '600' }}>Full Name</span>}
                      rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                      <Input
                        placeholder="John Doe"
                        style={{
                          background: themeStyles.inputBg,
                          border: `1px solid ${themeStyles.inputBorder}`,
                          color: themeStyles.titleColor,
                          borderRadius: '8px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="email"
                      label={<span style={{ color: themeStyles.titleColor, fontWeight: '600' }}>Email Address</span>}
                      rules={[
                        { required: true, message: 'Please enter your email' },
                        { type: 'email', message: 'Please enter a valid email' }
                      ]}
                    >
                      <Input
                        placeholder="john@example.com"
                        style={{
                          background: themeStyles.inputBg,
                          border: `1px solid ${themeStyles.inputBorder}`,
                          color: themeStyles.titleColor,
                          borderRadius: '8px',
                          padding: '12px 16px'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="phone"
                  label={<span style={{ color: themeStyles.titleColor, fontWeight: '600' }}>Phone Number</span>}
                  rules={[{ required: true, message: 'Please enter your phone number' }]}
                >
                  <Input
                    placeholder="+91 98765 43210"
                    style={{
                      background: themeStyles.inputBg,
                      border: `1px solid ${themeStyles.inputBorder}`,
                      color: themeStyles.titleColor,
                      borderRadius: '8px',
                      padding: '12px 16px'
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="subject"
                  label={<span style={{ color: themeStyles.titleColor, fontWeight: '600' }}>Subject</span>}
                  rules={[{ required: true, message: 'Please enter a subject' }]}
                >
                  <Input
                    placeholder="How can we help you?"
                    style={{
                      background: themeStyles.inputBg,
                      border: `1px solid ${themeStyles.inputBorder}`,
                      color: themeStyles.titleColor,
                      borderRadius: '8px',
                      padding: '12px 16px'
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="message"
                  label={<span style={{ color: themeStyles.titleColor, fontWeight: '600' }}>Message</span>}
                  rules={[{ required: true, message: 'Please enter your message' }]}
                >
                  <TextArea
                    rows={6}
                    placeholder="Tell us more about your inquiry..."
                    style={{
                      background: themeStyles.inputBg,
                      border: `1px solid ${themeStyles.inputBorder}`,
                      color: themeStyles.titleColor,
                      borderRadius: '8px',
                      padding: '12px 16px'
                    }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SendOutlined />}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '12px 48px',
                      height: 'auto',
                      fontSize: '16px',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                    }}
                  >
                    Send Message
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>
        </div>

        {/* Contact Info Cards - Order 2 on Mobile */}
        <div style={{
          order: isMobile ? 2 : 1,
          padding: isMobile ? '16px 12px' : '60px 24px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%'
        }}>
          <Row gutter={isMobile ? [16, 16] : [24, 24]}>
            {contactInfo.map((info, index) => (
              <Col xs={12} sm={12} lg={6} key={index}>
                <Card
                  style={{
                    background: themeStyles.cardBg,
                    border: `1px solid ${themeStyles.cardBorder}`,
                    borderRadius: '16px',
                    textAlign: 'center',
                    height: '100%',
                    backdropFilter: 'blur(20px)',
                    boxShadow: theme === 'dark'
                      ? '0 10px 40px rgba(0, 0, 0, 0.3)'
                      : '0 10px 40px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                  bodyStyle={{ padding: isMobile ? '24px 10px' : '32px 24px' }}
                  hoverable
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = theme === 'dark'
                      ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                      : '0 20px 60px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = theme === 'dark'
                      ? '0 10px 40px rgba(0, 0, 0, 0.3)'
                      : '0 10px 40px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <div style={{ marginBottom: isMobile ? '12px' : '20px' }}>
                    {React.cloneElement(info.icon, { style: { ...info.icon.props.style, fontSize: isMobile ? '32px' : '28px' } })}
                  </div>
                  <Title level={4} style={{
                    color: themeStyles.titleColor,
                    marginBottom: isMobile ? '6px' : '12px',
                    fontSize: isMobile ? '16px' : '18px',
                    lineHeight: 1.2
                  }}>
                    {info.title}
                  </Title>
                  {info.link ? (
                    <a
                      href={info.link}
                      style={{
                        color: themeStyles.textColor,
                        textDecoration: 'none',
                        fontSize: isMobile ? '13px' : '15px',
                        transition: 'color 0.2s',
                        wordBreak: 'break-word',
                        lineHeight: '1.4',
                        display: 'block'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = themeStyles.accentColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = themeStyles.textColor}
                    >
                      {info.content}
                    </a>
                  ) : (
                    <Paragraph style={{
                      color: themeStyles.textColor,
                      margin: 0,
                      fontSize: isMobile ? '13px' : '15px',
                      wordBreak: 'break-word',
                      lineHeight: '1.4'
                    }}>
                      {info.content}
                    </Paragraph>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Contact;
